import { createSlice } from '@reduxjs/toolkit';
import { ELocalStore, ESessionStore } from '../../data/Constants';
import { SessionStore } from '../../services/SessionStore';
import { LocalStore } from '../../services/LocalStore';

import type { IApplicationSettings } from '../../../types';

const params = new URLSearchParams(globalThis.location.search);
const configurationId = params.get('configurationId');
const sessionStoreKey = 'application-settings';

const defaultApplicationSettings: IApplicationSettings = {
  showSectionTabs: true,
  showSummary: true,
  activeTab: 0,
  forceShowSectionTabs: false,
  forceShowSummary: false,
  pageSize: {
    isExtraSmall: false,
    isSmall: false,
    isMedium: false,
    isLarge: false,
    isExtraLarge: false,
  },
  currency: {
    countryCode: 'US',
    name: 'United State Dollar',
    currencyCode: 'USD',
    locale: 'en-US',
  },
  isConfigurationChanged: false,
  priceListMap: 'US',
  productsActiveTab: {},
  showAutoSelections: true,
};

const getDefaultSettings = (): IApplicationSettings => {
  let storedSettings = SessionStore.get(sessionStoreKey);

  if (SessionStore.get(ESessionStore.ConfigurationId) !== configurationId) {
    //reset application setting for new confguration id
    SessionStore.remove(ESessionStore.ViewId);
    return defaultApplicationSettings;
  }
  if (storedSettings) {
    let flag = false;
    for (const key in defaultApplicationSettings) {
      if (!(key in storedSettings)) {
        flag = true;
        //set default settings if stored settings are missing any property
        storedSettings[key] = defaultApplicationSettings[key as keyof IApplicationSettings]; // add missing property in stored settings with default value
      }
    }
    if (flag) {
      // if property is missing then only update it in session store
      SessionStore.set(sessionStoreKey, storedSettings);
    }
  } else {
    storedSettings = defaultApplicationSettings;
  }
  return { ...storedSettings, forceShowSectionTabs: false, forceShowSummary: false };
};

const initialState = getDefaultSettings();

const applicationSettingsSlice = createSlice({
  name: 'applicationSettings',
  initialState,
  reducers: {
    setApplicationSettings(state, action) {
      state = { ...state, ...action.payload };
      SessionStore.set(sessionStoreKey, state);
      return state;
    },
    resetApplicationSettings() {
      SessionStore.set(sessionStoreKey, defaultApplicationSettings);
      return defaultApplicationSettings;
    },
    getApplicationSettings(state) {
      return state;
    },
    setActiveTab(state, action) {
      state.productsActiveTab[`${action.payload.modelId}`] = {
        sectionId: action.payload.sectionID,
        tabIndex: action.payload.tabIndex,
      };
      SessionStore.set(sessionStoreKey, state);
    },
    resetAllProductActiveTabs(state) {
      state.productsActiveTab = {};
      SessionStore.set(sessionStoreKey, state);
    },

    setCurrencyCode(state, action) {
      state.currency.currencyCode = action.payload.currencyCode;
      SessionStore.set(sessionStoreKey, state);
    },
    setIsConfigurationChanged(state, action) {
      state.isConfigurationChanged = action.payload.isConfigurationChanged;
      SessionStore.set(sessionStoreKey, state);
    },
    setCurrentActiveTab(state, action) {
      state.activeTab = action.payload.activeTab;
      SessionStore.set(sessionStoreKey, state);
    },
    updateAutoSelectedDialog(state, action) {
      state.showAutoSelections = action.payload.showAutoSelections;
      LocalStore.set(ELocalStore.ShowAutoSelections, action.payload.showAutoSelections);
    },
  },
});

export const {
  setApplicationSettings,
  resetApplicationSettings,
  getApplicationSettings,
  setActiveTab,
  setCurrencyCode,
  setIsConfigurationChanged,
  setCurrentActiveTab,
  resetAllProductActiveTabs,
  updateAutoSelectedDialog,
} = applicationSettingsSlice.actions;

export default applicationSettingsSlice.reducer;
